import { ChangeEvent, ForwardedRef, forwardRef, useState } from 'react';
import type { RegisterOptions } from 'react-hook-form';
import styled from 'styled-components';
import { CharacterCount } from '../../character_count';
import { Description } from '../base/description.component';
import { HelpText } from '../base/help_text.component';
import { Input, InputProps } from '../base/input.component';
import { Label } from '../base/label.component';
import { theme } from '../common_input.theme';
import { useInput } from '../use_input.hook';

export interface InputTextRootProps extends InputProps {
	/**
	 * The label of the input field.
	 */
	label: string;

	/**
	 * Use to visually hide the label.
	 */
	hideLabel?: boolean;

	name?: string;

	/**
	 * Use to add a descriptive text below the input field.
	 */
	helperText?: string;

	/**
	 * It's mandatory if `invalid` is set to `true` and only visible in that case
	 * too. The purpose with this is to provide a explanation why the input field is
	 * `invalid`. This text will replace the `helperText`, right below the text
	 * area.
	 */
	invalidText?: string;

	/**
	 * The maximum amount of characters allowed by the input
	 * Keep in mind that you still need to handle validation with `invalid` and `invalidText`
	 */
	characterCount?: number;

	/**
	 * This determines if the character-count should be shown even if the
	 * `characterCount` value has not been exceeded
	 */
	alwaysShowCharacterCount?: boolean;
	description?: string;
	options?: RegisterOptions;
}

const elements = {
	container: styled.div`
		display: flex;
		flex-direction: column;
		flex: 1 1 auto;
	`,
	label: styled(Label)`
		margin-bottom: ${theme.getValue('labelMarginBottom')};
	`,
	description: styled(Description)`
		margin-bottom: ${theme.getValue('descriptionMarginBottom')};
	`,
	additionalInformation: styled.span`
		display: flex;
		justify-content: space-between;
	`,
	footer: styled.div`
		display: flex;
		justify-content: space-between;
		gap: ${theme.getValue('helpTextGap')};
	`,
};

export const CellulaInputTextRoot = forwardRef(function CellulaInputTextRoot(
	{
		id,
		label,
		hideLabel,
		name,
		required,
		invalid,
		invalidText,
		helperText,
		readOnly,
		onChange,
		onBlur,
		defaultValue,
		characterCount,
		alwaysShowCharacterCount,
		description,
		optional,
		...rest
	}: InputTextRootProps,
	ref: ForwardedRef<HTMLInputElement>,
): JSX.Element {
	const [inputValue, setInputValue] = useState<string>(defaultValue?.toString() ?? '');
	function handleInputChange(event: ChangeEvent<HTMLInputElement>): void {
		setInputValue(event.target.value);

		onChange?.(event);
	}

	const {
		props: inputProps,
		descriptionProps,
		helpTextProps,
		labelProps,
		characterCountProps,
	} = useInput<HTMLInputElement>(
		{
			id,
			name,
			label,
			hideLabel,
			required,
			invalid,
			invalidText,
			helperText,
			readOnly,
			onChange: handleInputChange,
			onBlur,
			defaultValue,
			characterCount,
			alwaysShowCharacterCount,
			description,
			optional,
		},
		ref,
	);

	return (
		<elements.container>
			{/** biome-ignore lint/a11y/noLabelWithoutControl: suppressed due to migration - should be fixed */}
			<elements.label {...labelProps} />
			<elements.description {...descriptionProps} />

			<Input {...rest} {...inputProps} />

			<elements.footer>
				<HelpText {...helpTextProps} />

				<CharacterCount value={inputValue} {...characterCountProps} />
			</elements.footer>
		</elements.container>
	);
});
